/** @file Pose buffers (structure of arrays) and their channel layout (frozen contract). */

/** Strides and offsets of the per-bone, per-slot and per-constraint channel blocks. */
export const POSE_LAYOUT = {
  /** local/applied: x, y, rotation, scaleX, scaleY, shearX, shearY */
  LOCAL_STRIDE: 7,
  /** world: a, b, c, d, tx, ty */
  WORLD_STRIDE: 6,
  /** slot channels in Sample.values, all floats 0..1: r, g, b, darkR, darkG, darkB, alpha (mixers lerp per channel) */
  SLOT_STRIDE: 7,
  /** ik: mix (0..1), softness (px), bend (0/1 = bendPositive), stretch (0/1), compress (0/1) */
  IK_STRIDE: 5,
  /** Offsets inside a LOCAL_STRIDE block. */
  X: 0, Y: 1, ROTATION: 2, SCALE_X: 3, SCALE_Y: 4, SHEAR_X: 5, SHEAR_Y: 6,
  /** Offsets inside a SLOT_STRIDE block. */
  SLOT_R: 0, SLOT_G: 1, SLOT_B: 2, SLOT_DARK_R: 3, SLOT_DARK_G: 4, SLOT_DARK_B: 5, SLOT_ALPHA: 6,
  /** Offsets inside an IK_STRIDE block. */
  IK_MIX: 0, IK_SOFTNESS: 1, IK_BEND: 2, IK_STRETCH: 3, IK_COMPRESS: 4,
} as const;

/**
 * Per-frame state of one skeleton instance. Every buffer is allocated once by createPose (sized from
 * SkeletonData) and only overwritten afterwards: nothing here is ever null or reallocated per frame.
 */
export interface Pose {
  /** Setup + animation, per bone (LOCAL_STRIDE). */
  local: Float64Array;
  /** local after constraints, per bone (LOCAL_STRIDE). */
  applied: Float64Array;
  /** World affine per bone (WORLD_STRIDE). */
  world: Float64Array;
  /** InheritMode code per bone (0 normal … 4 noScaleOrReflection). */
  inherit: Uint8Array;
  /** Final tint per slot as a `math/color.pack` word (bytes R,G,B,A; alpha applied). */
  slotColor: Uint32Array;
  /** Dark tint per slot as a `math/color.pack` word (A unused). */
  slotDark: Uint32Array;
  /** Attachment index per slot (resolved through the active skins), or -1. */
  slotAttachment: Int32Array;
  /** Bone index override per slot (runtime equip), or -1. */
  slotBoneOverride: Int32Array;
  /** Extra affine per slot (WORLD_STRIDE) applied after the slot bone: runtime equip / direction remaps write it. */
  slotMount: Float64Array;
  /** 1 when slotMount[slot] is in use; 0 = identity (buildRender skips the multiply). */
  slotMountActive: Uint8Array;
  /** Slot indices in draw order. */
  drawOrder: Int32Array;
  /** Per constraint (IK_STRIDE). */
  ik: Float64Array;
  /** Deform offsets per attachment in compiled form (per-influence for skinned meshes); one buffer per mesh attachment, zero-filled by resetPose. */
  deform: Float64Array[];
  flipX: boolean;
}
